import React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { styled } from '@mui/material/styles';
import { SxProps, Theme } from '@mui/material/styles';

interface ScrollAreaProps {
  children: React.ReactNode;
  /** Height of the scroll area */
  height?: string | number;
  /** Scroll orientation - 'vertical', 'horizontal', or 'both' (default: 'vertical') */
  orientation?: 'vertical' | 'horizontal' | 'both';
  /** Always show the scrollbar (default: false - hides when not scrolling) */
  alwaysVisible?: boolean;
  /** Styles for the root container */
  rootSx?: SxProps<Theme>;
  /** Styles for the viewport (scrollable area) */
  viewportSx?: SxProps<Theme>;
  /** Styles for the scrollbar track */
  scrollbarSx?: SxProps<Theme>;
  /** Styles for the scrollbar thumb */
  thumbSx?: SxProps<Theme>;
}

// Base styled components - minimal styles, parent components provide customization
const StyledScrollAreaRoot = styled(ScrollAreaPrimitive.Root)({
  position: 'relative',
  overflow: 'hidden',
  width: '100%'
});

const StyledScrollAreaViewport = styled(ScrollAreaPrimitive.Viewport)<{
  $orientation?: 'vertical' | 'horizontal' | 'both';
}>(({ $orientation = 'vertical' }) => ({
  width: '100%',
  height: '100%',
  borderRadius: 'inherit',
  ...($orientation === 'vertical' && { overflowX: 'hidden' }),
  ...($orientation === 'horizontal' && { overflowY: 'hidden' }),
  // Radix creates an internal div - constrain its width
  '& > div': {
    display: 'block !important',
    width: '100%'
  }
}));

const StyledScrollAreaScrollbar = styled(ScrollAreaPrimitive.Scrollbar)<{
  $alwaysVisible?: boolean;
}>(() => ({
  display: 'flex',
  userSelect: 'none',
  touchAction: 'none',
  position: 'absolute'
}));

const StyledScrollAreaThumb = styled(ScrollAreaPrimitive.Thumb)({
  flex: 1,
  position: 'relative',
  // Increase hit area for easier grabbing
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    minWidth: '44px',
    minHeight: '44px'
  }
});

const StyledScrollAreaCorner = styled(ScrollAreaPrimitive.Corner)({
  backgroundColor: 'transparent'
});

/**
 * A custom scroll area component built on Radix UI primitives.
 * This is a base component - parent components should provide custom styling via sx props.
 * 
 * Usage:
 * ```tsx
 * <ScrollArea 
 *   height="400px"
 *   alwaysVisible
 *   scrollbarSx={{ right: 8, width: 6 }}
 *   thumbSx={{ backgroundColor: 'grey', borderRadius: '3px' }}
 * >
 *   {content}
 * </ScrollArea>
 * ```
 */
export const ScrollArea: React.FC<ScrollAreaProps> = ({
  children,
  height,
  orientation = 'vertical',
  alwaysVisible = false,
  rootSx = {},
  viewportSx = {},
  scrollbarSx = {},
  thumbSx = {}
}) => {
  const showVertical = orientation === 'vertical' || orientation === 'both';
  const showHorizontal = orientation === 'horizontal' || orientation === 'both';

  // Base scrollbar visibility styles
  const scrollbarVisibilitySx = alwaysVisible
    ? { opacity: 1 }
    : {
        opacity: 0,
        transition: 'opacity 0.2s ease',
        '&[data-state="visible"]': { opacity: 1 }
      };

  return (
    <StyledScrollAreaRoot
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      sx={rootSx}
    >
      <StyledScrollAreaViewport $orientation={orientation} sx={viewportSx}>
        {children}
      </StyledScrollAreaViewport>
      {showVertical && (
        <StyledScrollAreaScrollbar 
          orientation="vertical"
          forceMount={alwaysVisible ? true : undefined}
          sx={{ ...scrollbarVisibilitySx, ...scrollbarSx }}
        >
          <StyledScrollAreaThumb sx={thumbSx} />
        </StyledScrollAreaScrollbar>
      )}
      {showHorizontal && (
        <StyledScrollAreaScrollbar 
          orientation="horizontal"
          forceMount={alwaysVisible ? true : undefined}
          sx={{ ...scrollbarVisibilitySx, ...scrollbarSx }}
        >
          <StyledScrollAreaThumb sx={thumbSx} />
        </StyledScrollAreaScrollbar>
      )}
      {showVertical && showHorizontal && <StyledScrollAreaCorner />}
    </StyledScrollAreaRoot>
  );
};

export default ScrollArea;
